"use client";

import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  X,
  FileUp,
} from "lucide-react";
import {
  orderDeliveredAction,
} from "../../../services/action/manager";
import { uploadImageAction } from "../../../services/action/common";

const DeliverModal = ({ setShowDeliverModal, leadDetails }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authenticationReducer);
  const {isUpdating} =useSelector((state)=>state.commonReducer)
  const [deliveryMessage, setDeliveryMessage] = useState("");
  const [deliveryAmount, setDeliveryAmount] = useState("");
  const [deliveryType, setDeliveryType] = useState("");
  const [deliveryFiles, setDeliveryFiles] = useState([]);
  const [deliveryPaymentScreenshots, setDeliveryPaymentScreenshots] = useState(
    []
  );
  const [showDeliveryError, setShowDeliveryError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleDeliverySubmit = async () => {
    if (!deliveryType || deliveryFiles.length === 0) {
      setShowDeliveryError(true);
      return;
    }

    setIsUploading(true);

    try {
      const commonPayload = {
        _id: leadDetails._id,
        deliveryType,
        files: deliveryFiles,
        screenshots: deliveryPaymentScreenshots,
        message: deliveryMessage,
        amount:deliveryAmount
      };

      dispatch(
        orderDeliveredAction({
          userType: user.userType,
          data: commonPayload,
          cb:()=>{setShowDeliverModal(false)}
        })
      );
      
    } catch (error) {
      console.error("Error during delivery submission:", error);
      // Handle error (e.g., show error message to user)
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = async (e, type) => {
    const files = Array.from(e.target.files);
    setIsUploading(true);

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("images", file);

        const response = await new Promise((resolve) => {
          dispatch(
            uploadImageAction({
              data: formData,
              cb: resolve,
            })
          );
        });

        if (response?.imageUrls) {
          if (type === "delivery") {
            setDeliveryFiles((prev) => [...prev, ...response.imageUrls]);
          } else {
            setDeliveryPaymentScreenshots((prev) => [
              ...prev,
              ...response.imageUrls,
            ]);
          }
        }
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      // Handle error (e.g., show error message to user)
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-[500px] shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Deliver</h3>
          <button
            onClick={() => setShowDeliverModal(false)}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Balance Amount:{" "}
            {leadDetails.totalPayment - leadDetails.paymentRecieved} {leadDetails?.currancy}
          </div>

          {/* Rich Text Editor */}
          <div className="border rounded">
            <textarea
              value={deliveryMessage}
              onChange={(e) => setDeliveryMessage(e.target.value)}
              className="w-full p-3 h-32 focus:outline-none"
              placeholder="Enter your message..."
            />
          </div>

          {/* Upload Sections */}
          <div className="space-y-3">
            <div className="relative">
              <input
                type="file"
                onChange={(e) => handleFileUpload(e, "delivery")}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                multiple
              />
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:bg-gray-50">
                <FileUp className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-600">
                  Upload File<span className="text-red-500">*</span>
                </p>
                {deliveryFiles.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    {deliveryFiles.length} files selected
                  </p>
                )}
              </div>
            </div>

            <div className="relative">
              <input
                type="file"
                onChange={(e) => handleFileUpload(e, "payment")}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                multiple
              />
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:bg-gray-50">
                <FileUp className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-600">
                  Upload Payment Screenshots
                </p>
                {deliveryPaymentScreenshots.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    {deliveryPaymentScreenshots.length} screenshots selected
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Amount Input */}
          <div>
            <input
              type="number"
              value={deliveryAmount}
              onChange={(e) => setDeliveryAmount(e.target.value)}
              placeholder="Enter Amount"
              className="w-full border rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Delivery Type Dropdown */}
          <div className="space-y-1">
            <div className="relative">
              <select
                value={deliveryType}
                onChange={(e) => setDeliveryType(e.target.value)}
                className="w-full border rounded-lg p-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
              >
                <option value="">Select Delivery Type</option>
                <option value="full">Full Delivery</option>
                <option value="partial">Partial Delivery</option>
              </select>
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {showDeliveryError && !deliveryType && (
              <p className="text-red-500 text-sm">
                Please select a delivery type
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleDeliverySubmit}
            disabled={isUploading || isUpdating}
            className={`w-full bg-[#002B5B] text-white py-3 rounded-lg hover:bg-[#001a38] transition-colors font-medium ${
              isUploading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {(isUploading || isUpdating) ? "Please wait..." : "DELIVERED"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliverModal;
